import { PLATFORM_ID, PLATFORM_ID_TO_ENUM } from "../constants.js";
import { Platform, VMType } from "../enums.js";
import { getVirtualEnvironment, notEmpty } from "../utils/utils.js";
import { validateAndLowerCase } from "../utils/validateAndParseAddress.js";
/**
 * Converts a raw BigInt amount to a decimal string, accounting for token decimals.
 * Uses string manipulation to avoid precision loss with large numbers.
 * e.g., rawAmountToDecimalString(1000000n, 6) returns "1.000000"
 *       rawAmountToDecimalString(1000000n, 6, 2) returns "1.00"
 *
 * @param rawAmount The raw BigInt amount
 * @param decimals The number of decimals the token has
 * @param targetDecimals Number of decimal places to show in output (defaults to decimals)
 */
export const rawAmountToDecimalString = (rawAmount, decimals, targetDecimals) => {
    const outputDecimals = targetDecimals ?? decimals;
    // Handle zero decimals case
    if (decimals === 0) {
        return outputDecimals === 0 ? rawAmount.toString() : `${rawAmount}.${"0".repeat(outputDecimals)}`;
    }
    // Convert to string and pad with leading zeros if needed
    let amountStr = rawAmount.toString();
    if (amountStr.length <= decimals) {
        amountStr = amountStr.padStart(decimals + 1, "0");
    }
    // Insert decimal point
    const integerPart = amountStr.slice(0, -decimals) || "0";
    const fractionalPart = amountStr.slice(-decimals);
    // Adjust fractional part to target decimal places
    if (outputDecimals === 0) {
        return integerPart;
    }
    else if (outputDecimals >= decimals) {
        return `${integerPart}.${fractionalPart.padEnd(outputDecimals, "0")}`;
    }
    else {
        return `${integerPart}.${fractionalPart.slice(0, outputDecimals)}`;
    }
};
export const isCEXPlatform = (platformId) => {
    const platform = getPlatformFromPlatformId(platformId);
    return platform ? [Platform.OKX, Platform.COINBASE, Platform.KRAKEN, Platform.GEMINI].includes(platform) : false;
};
export const getExternalPlatformIdFromInternalPlatformId = (value) => {
    switch (getVirtualEnvironment(value)) {
        case VMType.SVM:
            return `svm-${value.toString()}`;
        case VMType.EVM:
            return `eip155-${value.toString()}`;
        case VMType.OFFCHAIN:
            return `offchain-${value.toString()}`;
        default:
            throw new Error(`Unsupported VM type: ${getVirtualEnvironment(value)}`);
    }
};
export const getInternalPlatformIdFromExternalPlatformId = (value) => {
    let platformId = undefined;
    const formattedValue = validateAndLowerCase(value);
    if (formattedValue?.includes("eip155")) {
        const publicIdElements = formattedValue.split("eip155-");
        const potentialId = publicIdElements?.length > 1 ? publicIdElements[1] : undefined;
        platformId = !Number.isNaN(Number(potentialId)) ? BigInt(potentialId) : undefined;
    }
    else if (formattedValue?.includes("svm")) {
        const publicIdElements = formattedValue.split("svm-");
        const potentialId = publicIdElements?.length > 1 ? publicIdElements[1] : undefined;
        platformId = !Number.isNaN(Number(potentialId)) ? BigInt(potentialId) : undefined;
    }
    else if (formattedValue?.includes("offchain")) {
        const publicIdElements = formattedValue.split("offchain-");
        const potentialId = publicIdElements?.length > 1 ? publicIdElements[1] : undefined;
        platformId = !Number.isNaN(Number(potentialId)) ? BigInt(potentialId) : undefined;
    }
    else if (formattedValue?.includes("all_vm_platforms")) {
        return undefined;
    }
    else if (formattedValue?.includes("all_evm_platforms")) {
        return undefined;
    }
    else {
        const platform = getPlatformFromName(formattedValue);
        const potentialId = platform ? getPlatformIdFromPlatform(platform) : undefined;
        platformId = potentialId ? BigInt(potentialId) : undefined;
    }
    // ALL_VM_PLATFORMS and ALL_EVM_PLATFORMS are special values representing all platforms
    // Return undefined for these since they don't map to a specific platform ID
    if (notEmpty(formattedValue) && ["all_vm_platforms", "all_evm_platforms"].includes(formattedValue)) {
        return undefined;
    }
    return platformId;
};
export const getPlatformFromName = (platformName) => {
    const formattedPlatformName = validateAndLowerCase(platformName)?.replace("-", "_");
    switch (formattedPlatformName) {
        //todo(tyler): add mainnet aliases
        case "bnbt": {
            return Platform.BINANCE_TESTNET;
        }
        case "matic_amoy": {
            return Platform.POLYGON_AMOY;
        }
        case "matic": {
            return Platform.POLYGON;
        }
        case "mainnet": {
            return Platform.ETHEREUM;
        }
        case "holesky": {
            return Platform.ETHEREUM_HOLESKY;
        }
        case "sepolia": {
            return Platform.ETHEREUM_SEPOLIA;
        }
        case "bnb": {
            return Platform.BINANCE;
        }
        case "bnbt": {
            return Platform.BINANCE_TESTNET;
        }
        default: {
            //statements;
            return formattedPlatformName;
        }
    }
};
export const getPlatformIdFromPlatform = (platform) => {
    return PLATFORM_ID[platform];
};
export const isLocal = (id) => {
    const platform = id ? PLATFORM_ID_TO_ENUM[Number(id)] : undefined;
    if (platform)
        return [Platform.LOCAL_CHAIN_0, Platform.LOCAL_CHAIN_1].includes(platform);
    return false;
};
export const getPlatformFromPlatformId = (id) => {
    const platform = id ? PLATFORM_ID_TO_ENUM[Number(id)] : undefined;
    if (platform)
        return platform;
    return undefined;
};
